import React, { useMemo, useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import {
  CenteredModal,
  TextArea,
  Input,
  SubmitButton,
  useToast,
  Chip,
  CheckboxesToArrayField,
} from 'components';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { ParticipationStatus, useContactParticipation } from '@app/api';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import { Trans, useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  submitButton: {
    textAlign: 'right',
  },
  button: {
    marginRight: theme.marginBase,
  },
}));

const validationSchema = Yup.object().shape({
  message: Yup.string().required(),
  subject: Yup.string().required(),
  status: Yup.array(),
});

interface ContactFormValues {
  message: string;
  subject: string;
  status: ParticipationStatus[];
}

interface Props {
  onRequestClose: () => void;
  isOpen: boolean;
  sessionId: string;
  status: ParticipationStatus[];
  studyType?: string;
}

export const EmailContactColumnModal = ({
  onRequestClose,
  isOpen,
  status,
  sessionId,
  studyType,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const { mutateAsync: contactParticipation } = useContactParticipation();

  const options = useMemo(
    () =>
      status.map((s) => ({
        label: t('sessions.details.monitoring.contactModal.filters.' + s, {
          context: studyType?.toLowerCase(),
        }),
        value: s,
      })),
    [status, studyType, t],
  );

  const onSubmit = useCallback(
    async (values: ContactFormValues) => {
      const { message, subject, status } = values;

      try {
        await contactParticipation({
          sessionId,
          status,
          message,
          subject,
        });
        toast.success(t('sessions.details.monitoring.contactModal.success'));
        onRequestClose();
      } catch (e) {
        toast.exception(e);
      }
    },
    [contactParticipation, sessionId, onRequestClose, toast, t],
  );

  if (!status || !status.length) {
    return null;
  }

  const initialValues: ContactFormValues = {
    message: t('sessions.details.monitoring.contactModal.message.template', {
      context: status[0],
    }),
    subject: t('sessions.details.monitoring.contactModal.subject.template', {
      context: status[0],
    }),
    status: status,
  };

  return (
    <CenteredModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('sessions.details.monitoring.contactModal.title')}
      large
      chip={
        <Chip color={getColorForParticipationStatus(status[0])}>
          <Trans
            i18nKey={`sessions.details.monitoring.columnTitle.${status[0]}`}
          />
        </Chip>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <Input
            name="subject"
            label={t('sessions.details.monitoring.contactModal.subject.label')}
          />
          <TextArea
            name="message"
            label={t('sessions.details.monitoring.contactModal.message.label')}
          />
          {status.length > 1 && (
            <CheckboxesToArrayField
              label={t(
                'sessions.details.monitoring.contactModal.filters.title',
              )}
              options={options}
              name="status"
            />
          )}
          <div className={classes.submitButton}>
            <SubmitButton
              color="green"
              small
              text={t('sessions.details.monitoring.contactModal.submit')}
            />
          </div>
        </Form>
      </Formik>
    </CenteredModal>
  );
};
